import {ObjectNode} from "#template/nodes/object/object-node"
import {z} from "zod/v4-mini"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {ExternalId, TemplateLikeNode} from "#template/nodes/types"
import {Parameters} from "#template/nodes/parameters"
import {externalId} from "#template/nodes/validation"
import {templateLike} from "#template/nodes/template-graph/fundamental"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {TemplateNode} from "#template/template-node/template-node"
import {TemplateGraph} from "#template/nodes/template-graph/template-graph"
import type {ObjectBounds} from "#template/geometry-processing/bounds"
import type {ObjectData} from "#template/interfaces/object-data"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import {CompileTemplate} from "#template/runtime-graph/nodes/compile-template"
import {FilterAndWrapInterface} from "#template/runtime-graph/nodes/filter-and-wrap-interface"
import {RunTemplate} from "#template/runtime-graph/nodes/run-template"
import type {TemplateContext, TemplateData} from "#template/compile-context"
import type {EvaluatedTemplateInputs} from "#template/fundamental/evaluated-input"
import {wrapComponentOutputsIds} from "#template/output-forwarding"
import {isEvaluatedTemplateNode} from "#template/utils/template-node-utils"
import type {Matrix4} from "@cm/math"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import {EMPTY_OBJECT} from "#template/runtime-graph/types"
import type {SolverObjectData} from "#template/runtime-graph/nodes/solver/object-data"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

export function onCompileTemplateInstanceImpl(
    scope: GraphBuilderScope,
    context: OnCompileContext,
    templateData: BuilderInlet<TemplateData | null>,
    inputsData: BuilderInlet<EvaluatedTemplateInputs>,
    claimedInputIds: BuilderInlet<string[]>,
    transformMatrix: BuilderInlet<Matrix4>,
    solverObject: BuilderInlet<SolverObjectData>,
    path: BuilderInlet<TemplateNode[]>,
    visible: boolean,
    nodeId: string,
    nodeLocalId: string,
    computeDummyReifiedMeshData: boolean,
    pushDescriptorList: boolean,
    shouldExposeClaimedSubTemplateInputs: boolean,
    inputSourceMapping?: Map<string, TemplateNode[]>,
) {
    const {evaluator, currentTemplate, templateDepth, subTemplates, sceneProperties, environment, render} = context
    const {allBounds} = currentTemplate
    const {
        readyList,
        preDisplayLists,
        displayLists,
        infoLists,
        solverDatas,
        descriptorLists,
        exposeClaimedSubTemplateInputs,
        transformAccessorLists,
        liveValueAccessorLists,
    } = subTemplates
    const {templateContext, templateScope} = evaluator

    const [graphValid, graphInvalid] = scope.branch(templateData)
    const graph = scope.phi(scope.get(graphValid, "graph"), graphInvalid)

    const objectId = scope.genNodeId(RunTemplate)

    const compileTemplate = scope.node(CompileTemplate, {
        templateContext: scope.pureLambda<[TemplateContext, Matrix4, string, boolean], TemplateContext>(
            scope.tuple(templateContext, transformMatrix, nodeId, computeDummyReifiedMeshData),
            ([templateContext, transformMatrix, id, computeDummyReifiedMeshData]) => {
                if (templateContext.instanceIdChain.includes(id))
                    throw new Error(`Circular template instance detected: ${templateContext.instanceIdChain.join(" -> ")} -> ${id}`)

                return {
                    ...templateContext,
                    instanceIdChain: [...templateContext.instanceIdChain, id],
                    templateMatrix: transformMatrix,
                    computeDummyReifiedMeshData,
                }
            },
            "templateContext",
        ),
        graph,
        sceneProperties: sceneProperties ?? null,
        render: render ?? null,
        environment: environment ?? null,
        inputs: inputsData,
        path,
        compiledTemplateId: objectId,
        templateDepth: templateDepth + 1,
        exposeClaimedSubTemplateInputs: shouldExposeClaimedSubTemplateInputs,
        overrideMaterial: context.overrideMaterial ?? null,
        contextOtherData: context.otherData ?? null,
    })
    const runTemplate = scope.node(RunTemplate, {
        $id: objectId,
        compiledTemplate: compileTemplate.compiledTemplate,
    })

    readyList.push(runTemplate.ready)

    transformAccessorLists.push(runTemplate.transformAccessorList)
    liveValueAccessorLists.push(runTemplate.liveValueAccessorList)

    if (visible) {
        preDisplayLists.push(runTemplate.preDisplayList)
        displayLists.push(runTemplate.displayList)
    }
    solverDatas.push(runTemplate.solverData)

    if (pushDescriptorList) {
        descriptorLists.push(
            scope.node(FilterAndWrapInterface, {
                interface: runTemplate.descriptorList,
                wrapWithId: nodeId,
                claimedInputIds,
                includeClaimed: exposeClaimedSubTemplateInputs,
            }).output,
        )
        if (inputSourceMapping) {
            subTemplates.inputSourceMappings.push({mapping: inputSourceMapping, rawDescriptorList: runTemplate.descriptorList, nodeId})
        }

        subTemplates.subTemplatesOutputs.push(scope.phi(wrapComponentOutputsIds(scope, runTemplate.outputs, nodeId, "templates"), EMPTY_OBJECT))
    }

    infoLists.push(runTemplate.infoList)
    subTemplates.nodeToInfoMaps.push(runTemplate.nodeToInfoMap)

    templateScope.alias(runTemplate.outputs, `templateOutputs-${nodeLocalId}`)

    const dimensionGuideBounds = scope.pureLambda(
        scope.tuple(runTemplate.dimensionGuideBounds, transformMatrix, nodeId),
        ([dimensionGuideBounds, transformMatrix, nodeId]): ObjectBounds => {
            const objectId = dimensionGuideBounds.objectId !== "" ? dimensionGuideBounds.objectId : nodeId
            return {...dimensionGuideBounds, objectId, matrix: transformMatrix.toArray()}
        },
        "getDimensionGuideBounds",
    )

    allBounds.push(runTemplate.bounds)
    allBounds.push(dimensionGuideBounds)

    const objectData = scope.struct<ObjectData>("ObjectData", {
        id: objectId,
        path,
        matrix: transformMatrix,
        solverObject,
        bounds: runTemplate.bounds,
        dimensionGuideBounds,
        preDisplayList: runTemplate.preDisplayList,
        displayList: runTemplate.displayList,
        solverData: runTemplate.solverData,
        visible,
    })

    templateScope.alias(objectData, `objectData-${nodeLocalId}`)

    return runTemplate
}

export type TemplateInstanceParameters = {
    name: string
    id: ExternalId
    template: TemplateLikeNode
    parameters: Parameters
} & ObjectNodeParameters

const templateInstanceDefinition: ObjectNodeDefinition<TemplateInstanceParameters> = {
    nodeClass: "TemplateInstance",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                id: externalId,
                template: templateLike,
                parameters: graphNode(Parameters),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class TemplateInstance extends ObjectNode<TemplateInstanceParameters> {
    public override get definition() {
        return templateInstanceDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<TemplateInstanceParameters> {
        const {evaluator, subTemplates, path} = context
        const {lookupByExternalIdPathMap} = subTemplates
        const {templateContext, activeNodeSet} = evaluator
        const {template, id, visible} = this.parameters

        const scope = evaluator.getScope(this)
        const templateData = evaluator.evaluate(template)
        const [inputs, claimedInputIds] = evaluator.evaluateTemplateInputs(scope.scope("parameters"), this)
        const [transformMatrix, solverObject] = this.setupTransform(scope, context, undefined)
        const newPath = scope.pureLambda(scope.tuple(path, this), ([path, instance]) => [...path, instance], "newPath", {
            output: TD.ObjectPath,
        })
        const localId = evaluator.getLocalId(this)
        const computeDummyReifiedMeshData = templateContext.computeDummyReifiedMeshData ? !isEvaluatedTemplateNode(this, activeNodeSet) : false

        const inputSourceMapping = new Map<string, TemplateNode[]>()
        for (const [inputId, value] of Object.entries(this.parameters.parameters.parameters)) {
            if (value === undefined || value === null) continue
            const sourceNodes: TemplateNode[] = []
            if (value instanceof TemplateNode) sourceNodes.push(value)
            else if (Array.isArray(value))
                for (const item of value as unknown[]) {
                    if (item instanceof TemplateNode) sourceNodes.push(item)
                }
            if (sourceNodes.length > 0) inputSourceMapping.set(inputId, sourceNodes)
        }

        const {lookupByExternalIdPath} = onCompileTemplateInstanceImpl(
            scope,
            context,
            templateData,
            inputs,
            claimedInputIds,
            transformMatrix,
            solverObject,
            newPath,
            visible,
            id,
            localId,
            computeDummyReifiedMeshData,
            true,
            false,
            inputSourceMapping,
        )

        lookupByExternalIdPathMap.set(this, lookupByExternalIdPath)

        return visitNone(this.parameters)
    }
}

export type LocalTemplateInstanceParameters = {
    name: string
    id: ExternalId
    template: TemplateGraph
    parameters: Parameters
    parent?: TemplateNode
} & ObjectNodeParameters

const localTemplateInstanceDefinition: ObjectNodeDefinition<LocalTemplateInstanceParameters> = {
    nodeClass: "LocalTemplateInstance",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                id: externalId,
                template: graphNode(TemplateGraph),
                parameters: graphNode(Parameters),
                parent: z.optional(graphNode(TemplateNode)),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class LocalTemplateInstance extends ObjectNode<LocalTemplateInstanceParameters> {
    public override get definition() {
        return localTemplateInstanceDefinition
    }
}
